import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faArrowRight, faTimes } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { HeadingItem } from 'components/heading-item/heading-item';
import { DbWorkerMessage, DbWorkerMessageType } from 'contexts/db-state/db-worker';
import { Button, Popup, TextArea } from 'devextreme-react';
import notify from "devextreme/ui/notify";
import React, { FC, Fragment, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRequestCache } from "services";
import { AppStateType } from 'services/enums';
import { dbWorker } from 'utils';
import './help-popup.scss';

type Props = {
    isVisible: boolean,
    onClose: () => void,
}

enum HelpAction {
    ResetData = 'ResetData',
    ReportIssue = 'ReportIssue',
    None = 'None',
}

export const HelpPopup: FC<Props> = ({ isVisible, onClose }) => {

    const [comment, setComment] = useState<string>('')
    const [selectedAction, setSelectedAction] = useState<HelpAction>(HelpAction.None)

    const navigate = useNavigate();

    const { sendCacheAppState } = useRequestCache()
    const messageTimeMs = 3000;
    const offlineMessageTimeMs = 6000;

    const [userEnteredPassword, setUserEnteredPassword] = useState<string>('')
    const [passwordIsCorrect, setPasswordIsCorrect] = useState<boolean | null>(null)
    const password = "Resetdata2022";

    const saveAppCache = async (): Promise<boolean> => {
        notify('Sending issue report...', 'info', messageTimeMs)

        try {
            const type: AppStateType = selectedAction === HelpAction.ResetData ? AppStateType.DataReset : AppStateType.IssueReport
            await sendCacheAppState(type, comment)

            if (navigator.onLine)
                notify('Issue report sent. Thank you!', 'success', messageTimeMs)

            else
                notify('It looks like you are offline, the app state will be sent when you come back online', 'info', offlineMessageTimeMs)

            return true
        }
        catch (e) {
            notify('Could not send issue report. Check your internet.', 'error', messageTimeMs)
            console.error(e)
            return false
        }
    }

    const resetData = async (): Promise<void> => {
        if(await saveAppCache()) {
            notify('Resetting data. This may take a few moments.', 'info', messageTimeMs)
            const swRegistration: ServiceWorkerRegistration | undefined = await navigator.serviceWorker.getRegistration()
            if (swRegistration) {
              swRegistration.unregister()
              const cacheNames: string[] = await caches.keys()
              await Promise.all(cacheNames.map(async cacheName => caches.delete(cacheName)))
            }
      
            dbWorker.addEventListener('message', e => {
              const message = e.data as DbWorkerMessage
              if (message.type === DbWorkerMessageType.DeleteDb) {
                navigate('/logout')
              }
            })
            await dbWorker.postMessage({ type: DbWorkerMessageType.DeleteDb })
        }
      }

    const confirmSelectedAction = async () => {
        onClose()

        switch (selectedAction) {
            case HelpAction.ReportIssue: await saveAppCache(); break;
            case HelpAction.ResetData: resetData(); break;
            default: break
        }
    }

    const checkIfEnteredPasswordIsCorrect = () => {
        if (userEnteredPassword == password) {
            setPasswordIsCorrect(true)
        }

        else {
            setPasswordIsCorrect(false)
        }
    }

    const menuItems = [
        {
            text: "Report an issue",
            onClick: () => setSelectedAction(HelpAction.ReportIssue)
        },
        {
            text: "Reset my data",
            onClick: () => setSelectedAction(HelpAction.ResetData)
        }
    ]

    const buttonColor = '#8b8b8b'

    return (
        <Popup
            visible={isVisible}
            dragEnabled={false}
            closeOnOutsideClick={false}
            showTitle={false}
            width={330}
            height={400}
        >
            <Fragment>
                <HeadingItem text={'Help'} rightHandIcon={{ icon: faTimes, size: 'lg', color: buttonColor }} onClick={onClose}/>
                <div>
                    { selectedAction == HelpAction.None ?
                        <>
                            { menuItems.map((item, index) => (
                                <div className={'debug-menu'} key={index} onClick={item.onClick}>
                                    <div className={'debug-menu-text'}>{item.text}</div>
                                    <FontAwesomeIcon icon={faArrowRight as IconProp} size={'lg'} color={buttonColor} />
                                </div>
                            ))}
                        </>
                    :
                        <>
                            {selectedAction === HelpAction.ResetData && !passwordIsCorrect ?
                                <div className='debug-menu-comment-container'>
                                    <div>Enter the password to reset the data.</div>
                                    <div className={'radio-button-text-area'}>
                                        <TextArea 
                                            height={60} 
                                            value={userEnteredPassword}
                                            onValueChanged={(e) => setUserEnteredPassword(e.value)}
                                        />
                                    </div>
                                    <Button
                                        icon={'arrowright'}
                                        text={'Check password'}
                                        onClick={() => checkIfEnteredPasswordIsCorrect()} 
                                    />
                                    {passwordIsCorrect == false &&
                                        <div className='debug-menu-comment-container-error-message'>Incorrect password</div>
                                    }
                                </div>
                                :
                                <div className='debug-menu-comment-container'>
                                    <div>Sorry to hear you're having issues. Please provide some more information about the issue you encountered.</div>
                                    <div className={'debug-menu-comment'}>
                                        <TextArea 
                                            width={'100%'} 
                                            height={100} 
                                            value={comment}
                                            onValueChanged={(e) => setComment(e.value)}
                                        />
                                    </div>
                                    <Button
                                        icon={'arrowright'} 
                                        text={`${selectedAction === HelpAction.ResetData ? 'Reset': 'Send'}`}
                                        onClick={confirmSelectedAction}
                                    />
                                </div>
                            }
                        </>
                    }
                </div>
            </Fragment>
        </Popup>
    )
}